/**
 * KpiTooltip.tsx
 * Hover popover for computed KPI values.
 * Wraps any rendered number — on desktop hover (300ms delay), shows a popover
 * with the metric name, formula, component values, and description.
 * No visual change to the number itself.
 *
 * Usage:
 *   <KpiTooltip kpiKey="gross_margin_pct" projectId={projectId}>
 *     70.4%
 *   </KpiTooltip>
 *
 *   // With component values for formula breakdown:
 *   <KpiTooltip
 *     kpiKey="gross_margin_pct"
 *     projectId={projectId}
 *     componentValues={[
 *       { label: "Gross Profit", display: "$3,800" },
 *       { label: "Net Revenue",  display: "$5,400" },
 *     ]}
 *   >
 *     70.4%
 *   </KpiTooltip>
 */

import React, { useState, useRef, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useKpiDefinitions } from './useKpiDefinitions';

export interface ComponentValue {
  label: string;
  display: string;
}

interface KpiTooltipProps {
  kpiKey: string;
  projectId: string;
  componentValues?: ComponentValue[];
  enabled?: boolean;
  children: React.ReactNode;
}

const HOVER_DELAY = 300;
const POPOVER_GAP = 8;

export default function KpiTooltip({
  kpiKey,
  projectId,
  componentValues,
  enabled = true,
  children,
}: KpiTooltipProps) {
  const defs = useKpiDefinitions(projectId);
  const [visible, setVisible] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number; flipped: boolean }>({
    top: 0,
    left: 0,
    flipped: false,
  });
  const triggerRef = useRef<HTMLSpanElement>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const def = defs?.[kpiKey];

  const show = useCallback(() => {
    if (!triggerRef.current || !def) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const spaceAbove = rect.top;
    const flipped = spaceAbove < 160;

    setPos({
      top: flipped ? rect.bottom + POPOVER_GAP : rect.top - POPOVER_GAP,
      left: rect.left + rect.width / 2,
      flipped,
    });
    setVisible(true);
  }, [def]);

  const handleEnter = useCallback(() => {
    timerRef.current = setTimeout(show, HOVER_DELAY);
  }, [show]);

  const handleLeave = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    setVisible(false);
  }, []);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  // No definition or disabled — render children unchanged
  if (!enabled || !defs || !def) {
    return <>{children}</>;
  }

  return (
    <>
      <span
        ref={triggerRef}
        onMouseEnter={handleEnter}
        onMouseLeave={handleLeave}
      >
        {children}
      </span>

      {visible &&
        createPortal(
          <div
            onMouseEnter={() => {
              if (timerRef.current) clearTimeout(timerRef.current);
            }}
            onMouseLeave={handleLeave}
            className="fixed z-[9999] pointer-events-auto"
            style={{
              top: pos.flipped ? pos.top : undefined,
              bottom: pos.flipped ? undefined : `calc(100vh - ${pos.top}px)`,
              left: pos.left,
              transform: 'translateX(-50%)',
              maxWidth: 340,
              minWidth: 220,
            }}
          >
            <div className="bg-gray-800 border border-gray-700 rounded-lg shadow-xl px-4 py-3 text-left">
              {/* Name */}
              <p className="text-xs font-bold text-white mb-1">{def.name}</p>

              {/* Formula */}
              <p className="text-[11px] text-blue-400 font-mono mb-1.5">{def.formula}</p>

              {/* Component values (optional) */}
              {componentValues && componentValues.length > 0 && (
                <div className="flex flex-wrap gap-x-3 gap-y-0.5 mb-1.5">
                  {componentValues.map((cv, i) => (
                    <span key={i} className="text-[10px] text-gray-400">
                      <span className="text-gray-500">{cv.label}</span>{' '}
                      <span className="text-gray-300 font-medium">{cv.display}</span>
                    </span>
                  ))}
                </div>
              )}

              {/* Description */}
              <p className="text-[11px] text-gray-500 leading-relaxed">{def.description}</p>
            </div>
          </div>,
          document.body,
        )}
    </>
  );
}
